import MDEditor from "@uiw/react-md-editor";
import { Controller } from "react-hook-form";
import type {
  Control,
  ControllerRenderProps,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormRegister,
} from "react-hook-form";
import styles from "./Markdown.module.scss";
import withLabelAndError from "../withLabelAndError";

const excludeCommands = [
  "quote",
  "edit",
  "preview",
  "code",
  "codeBlock",
  "fullscreen",
  "help",
  "comment",
  "checked-list",
];

export interface MarkdownProps<T extends FieldValues = FieldValues> {
  control?: Control<T>;
  register?: UseFormRegister<T>;
  validations?: RegisterOptions<T, Path<T>>;
  name: Path<T>;
  placeholder?: string;
  [key: string]: unknown;
}

const Markdown = <T extends FieldValues = FieldValues>({
  control,
  register,
  name,
  validations,
  placeholder: _placeholder,
  ...rest
}: MarkdownProps<T>) => {
  const filterCommands = (command: { name?: string }) => {
    return !excludeCommands.includes(command?.name ?? "") && command;
  };

  const renderMarkdown = (
    field: Partial<ControllerRenderProps<T, Path<T>>> = {},
  ) => (
    <MDEditor
      {...rest}
      commandsFilter={filterCommands}
      value={(field.value as string) || ""}
      className={styles.inputField}
      onChange={(val) => field.onChange?.(val || "")}
      height={200}
    />
  );

  if (control) {
    return (
      <Controller
        control={control}
        {...(validations ? { rules: validations } : {})}
        name={name}
        render={({ field }) => renderMarkdown(field)}
      />
    );
  } else if (register) {
    const field = register(name, validations);
    return renderMarkdown(field as unknown as Partial<ControllerRenderProps<T, Path<T>>>);
  } else {
    return renderMarkdown();
  }
};

const MarkdownWithLabelAndError = withLabelAndError(Markdown);

export default MarkdownWithLabelAndError;
export { Markdown };
